// Popped studio card (spec §6.2, prototype `.card`): keeps the complete card
// treatment while accepting the organization gradient from its parent section.
// Presentational and pure — props in, JSX out — so it snapshot-tests without
// the depot. Per rule: think-in-systems (ai.knowledge/global).
// See knowledge: design-system-tailwind-color-tokens (studio.client) — only
// semantic tokens (bg-base-*, text-muted, font-demibold, rounded-12) emit CSS.
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { Room } from '@studio/models/room';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import type { StudioPickerDescriptor } from '../types.js';

export interface StudioCardProps {
  /** Theme-aware organization gradient for non-empty avatars. */
  readonly avatarGradientClassName?: string;
  readonly descriptor: StudioPickerDescriptor;
  /** Duplicate-name qualifier; screen readers hear it, the card never shows it. */
  readonly disambiguator?: string | undefined;
  /** Marks the studio the consumer already has open (header switch modal). */
  readonly isCurrent?: boolean;
  readonly actionLabel?: 'Open' | 'Link' | undefined;
  readonly disabled?: boolean;
  readonly onSelect: (roomId: Room['id']) => void;
}

// Meta line (prototype `.meta`): devices + optional cameras + live-ness.
// cameraCount is optional (spec §4.1) — when unknown the chip is omitted, a
// placeholder is never rendered.
const metaText = (descriptor: StudioPickerDescriptor): string => {
  if (descriptor.deviceCount === 0) {
    return 'No devices yet';
  }

  const parts = [`${descriptor.deviceCount} device${descriptor.deviceCount === 1 ? '' : 's'}`];

  if (descriptor.cameraCount !== undefined) {
    parts.push(`${descriptor.cameraCount} camera${descriptor.cameraCount === 1 ? '' : 's'}`);
  }

  parts.push(descriptor.status === 'online' ? 'live' : 'all offline');

  return parts.join(' · ');
};

/** One popped studio card. Rendered as a real button (A11y-1). */
export const StudioCard = ({
  avatarGradientClassName,
  descriptor,
  disambiguator,
  isCurrent,
  actionLabel: requestedActionLabel,
  disabled = false,
  onSelect
}: StudioCardProps): ReactElement => {
  const { room, status, isThisDevice, deviceCount, roles, bridgeStatus } = descriptor;
  const isEmpty = bridgeStatus === undefined && deviceCount === 0;
  const actionLabel = requestedActionLabel ?? (isEmpty ? 'Set up' : 'Open');
  const thisDevice = bridgeStatus?.kind === 'linked' ? bridgeStatus.isThisDevice : isThisDevice;

  return (
    <button
      type='button'
      // STag carries the room id so tests can target a specific card.
      // Per rule: playwright-stag-selectors-only (ai.knowledge/platform).
      {...STagNew(Sel.StudioPicker, 'studio_card', room.id)}
      // The duplicate-name qualifier is announced here, never shown on the
      // card, so same-named Studios stay distinct for screen readers.
      aria-label={
        disambiguator === undefined
          ? undefined
          : `${actionLabel} ${room.name}, ${disambiguator}, ${metaText(descriptor)}`
      }
      disabled={disabled}
      onClick={(): void => onSelect(room.id)}
      className={twMerge(
        'group flex min-h-[178px] w-[248px] shrink-0 cursor-pointer flex-col gap-10 rounded-12 p-[14px] text-left',
        'border border-white/5 bg-base-100 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]',
        'transition-all duration-[180ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none',
        'hover:-translate-y-2 hover:border-white/[0.18] hover:bg-base-50',
        'hover:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06),0_4px_12px_rgba(0,0,0,0.45),0_1px_2px_rgba(0,0,0,0.4)]',
        'active:translate-y-0 motion-reduce:hover:translate-y-0',
        'focus-visible:border-focus focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus/[0.35]',
        'disabled:opacity-60 disabled:cursor-wait disabled:hover:translate-y-0',
        // Empty studios read as a dashed "set up" affordance (prototype .card.empty).
        isEmpty && 'border-dashed border-white/10 bg-transparent hover:bg-base-200',
        // The already-open studio gets the primary outline marker (prototype .card.is-open).
        isCurrent && 'ring-primary border-primary ring-[1px]'
      )}
    >
      <span className='flex items-start justify-between gap-8'>
        <span
          // Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — locate avatars by room id.
          {...DataSTag(Sel.StudioPicker, 'studio_avatar', room.id)}
          className={twMerge(
            'flex h-40 w-40 shrink-0 items-center justify-center rounded-8 text-[16px] font-demibold tracking-[-0.01em] text-white',
            isEmpty
              ? 'bg-base-50 text-secondary'
              : (avatarGradientClassName ?? 'bg-gradient-to-br from-primary to-focus')
          )}
        >
          {room.name.charAt(0)}
        </span>

        {thisDevice ? (
          <span
            // The room-qualified marker lets automation identify the local
            // Bridge's Studio without relying on visual order or private copy.
            // Per rule: playwright-stag-selectors-only (ai.knowledge/platform).
            {...DataSTag(Sel.StudioPicker, 'this_device_badge', room.id)}
            className='shrink-0 whitespace-nowrap rounded-full bg-accent px-[7px] py-[3px] text-[9px] font-demibold uppercase leading-100 tracking-[0.06em] text-dark'
          >
            This device
          </span>
        ) : (
          !isEmpty && (
            // Text equivalent for the color dot (A11y-3): status is announced,
            // not only colored.
            <span
              aria-label={status === 'online' ? 'Studio online' : 'Studio offline'}
              role='img'
              className={twMerge(
                'mt-6 h-8 w-8 shrink-0 rounded-full',
                status === 'online'
                  ? 'bg-success shadow-[0_0_0_3px_rgba(41,204,106,0.18)]'
                  : 'bg-secondary'
              )}
            />
          )
        )}
      </span>

      <span className='line-clamp-2 text-[15px] font-demibold leading-[1.25] tracking-[-0.01em] text-light'>
        {room.name}
      </span>

      {roles.length > 0 && (
        <span className='flex flex-wrap gap-[5px]'>
          {roles.map((role) => (
            <span
              key={role.id}
              className='border-white/20 rounded-full border px-[7px] py-[3px] text-[9px] font-demibold uppercase leading-100 tracking-[0.06em] text-muted'
            >
              {role.label}
            </span>
          ))}
        </span>
      )}

      {bridgeStatus === undefined ? (
        <span
          className={twMerge('text-[12px] leading-130', isEmpty ? 'text-secondary' : 'text-muted')}
        >
          {metaText(descriptor)}
        </span>
      ) : bridgeStatus.kind === 'available' ? (
        <span className='text-[12px] font-demibold leading-130 text-success'>Available</span>
      ) : (
        <span
          className={twMerge(
            'text-[12px] font-demibold leading-130',
            bridgeStatus.isThisDevice ? 'text-accent' : 'text-warning'
          )}
        >
          Bridge Linked ·{' '}
          {bridgeStatus.kind === 'multibridge'
            ? `${bridgeStatus.machineNames.length} bridges`
            : bridgeStatus.isThisDevice
              ? 'This device'
              : bridgeStatus.machineName}
        </span>
      )}

      <span className='mt-auto flex items-center justify-end gap-4 pt-2 text-[11px] font-demibold uppercase tracking-[0.03em] text-secondary group-hover:text-light'>
        {actionLabel}
        <svg
          aria-hidden='true'
          className='transition-transform duration-[180ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:translate-x-2 motion-reduce:transition-none'
          width='15'
          height='15'
          viewBox='0 0 16 16'
          fill='none'
        >
          <path
            d='M6 4l4 4-4 4'
            stroke='currentColor'
            strokeWidth='1.6'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </span>
    </button>
  );
};
